/**
 * PaginaConvite - Página para aceitar convite de organização
 *
 * CONTEXTO DE NEGÓCIO:
 * Esta página é acessada através de um link de convite enviado por email.
 * Permite que o usuário:
 * - Veja detalhes do convite (organização, quem convidou, role)
 * - Aceite o convite e seja adicionado à organização
 *
 * FLUXO:
 * 1. Usuário recebe link por email: /convite/{token}
 * 2. Página carrega detalhes do convite (endpoint público)
 * 3. Se não estiver logado, redireciona para login com returnUrl
 * 4. Se estiver logado, mostra botão para aceitar
 * 5. Ao aceitar, é adicionado à organização e redirecionado
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAutenticacao } from '../contextos/ContextoAutenticacao';
import { useOrganizacao } from '../contextos/ContextoOrganizacao';
import { obterConvitePorToken, aceitarConvite } from '../servicos/servicoOrganizacao';
import type { ConviteOrganizacao } from '../tipos/tiposOrganizacao';
import { labelRole } from '../tipos/tiposOrganizacao';

export const PaginaConvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { estaAutenticado, estaCarregando: carregandoAuth } = useAutenticacao();
  const { carregarOrganizacoes, selecionarOrganizacao } = useOrganizacao();

  // Estados
  const [convite, setConvite] = useState<ConviteOrganizacao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aceitando, setAceitando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);

  // Carregar detalhes do convite
  useEffect(() => {
    async function carregarConvite() {
      if (!token) {
        setErro('Token de convite não fornecido');
        setCarregando(false);
        return;
      }

      try {
        const dados = await obterConvitePorToken(token);
        setConvite(dados);
      } catch (e) {
        setErro(e instanceof Error ? e.message : 'Convite não encontrado ou expirado');
      } finally {
        setCarregando(false);
      }
    }

    carregarConvite();
  }, [token]);

  // Redirecionar para login se não autenticado (após carregar convite)
  useEffect(() => {
    if (!carregando && !carregandoAuth && convite && !estaAutenticado) {
      // Salvar URL atual para retornar após login
      const returnUrl = encodeURIComponent(location.pathname);
      navigate(`/login?returnUrl=${returnUrl}`);
    }
  }, [carregando, carregandoAuth, convite, estaAutenticado, location.pathname, navigate]);

  // Handler para aceitar convite
  async function handleAceitarConvite() {
    if (!token) return;

    setAceitando(true);
    setErro(null);
    try {
      const resposta = await aceitarConvite(token);
      setSucesso(true);
      
      // Recarregar organizações e selecionar a nova
      await carregarOrganizacoes();
      selecionarOrganizacao(resposta.organizacao_id);
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/organizacao');
      }, 2000);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Erro ao aceitar convite');
    } finally {
      setAceitando(false);
    }
  }

  // ===== Render =====

  // Loading
  if (carregando || carregandoAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando convite...</p>
        </div>
      </div>
    );
  }

  // Erro
  if (erro && !convite) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Convite Inválido</h1>
          <p className="text-gray-600 mb-6">{erro}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Ir para a página inicial
          </button>
        </div>
      </div>
    );
  }

  // Sucesso
  if (sucesso) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg text-center">
          <div className="text-green-500 text-5xl mb-4">✓</div>
          <h1 className="text-2xl font-bold mb-2">Convite Aceito!</h1>
          <p className="text-gray-600 mb-2">
            Você agora é membro de <strong>{convite?.nome_organizacao}</strong>.
          </p>
          <p className="text-sm text-gray-500">Redirecionando...</p>
        </div>
      </div>
    );
  }

  // Aguardando autenticação
  if (!estaAutenticado) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Redirecionando para login...</p>
        </div>
      </div>
    );
  }

  // Exibir convite
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-6">
          <div className="text-blue-500 text-5xl mb-4">📬</div>
          <h1 className="text-2xl font-bold">Convite para Organização</h1>
        </div>

        {convite && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h2 className="font-semibold text-lg">{convite.nome_organizacao}</h2>
              <p className="text-sm text-gray-600 mt-2">
                <strong>{convite.nome_convidado_por}</strong> convidou você para participar desta organização.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Seu role:</span>
                <p className="font-medium">{labelRole(convite.role_atribuido)}</p>
              </div>
              <div>
                <span className="text-gray-500">Expira em:</span>
                <p className="font-medium">
                  {new Date(convite.expira_em).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>

            {erro && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {erro}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleAceitarConvite}
                disabled={aceitando}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {aceitando ? 'Aceitando...' : 'Aceitar Convite'}
              </button>
              <button
                onClick={() => navigate('/')}
                disabled={aceitando}
                className="px-4 py-3 bg-gray-200 rounded-lg font-medium hover:bg-gray-300"
              >
                Recusar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaginaConvite;
